import { formatMoney } from '@theme/utilities';

const BUNDLE_UPDATED_EVENT = 'bundle-builder:updated';

class BundleBuilder extends HTMLElement {
  constructor() {
    super();
    this.offers = [];
    this.activeOffer = null;
    this.productForm = null;
    this.message = null;
    this.offerCatalogs = new WeakMap();
    this.isSubmitting = false;
    this.pendingBundleId = null;

    this.handleChange = this.handleChange.bind(this);
    this.handleShopifyBlockSelect = this.handleShopifyBlockSelect.bind(this);
  }

  connectedCallback() {
    this.productForm = this.closest('product-form');
    if (this.productForm) {
      this.productForm.dataset.bundleStickyMobile = this.dataset.stickyMobile || 'false';
    }
    this.message = this.querySelector('[data-ref="bundle-message"]');
    this.offers = Array.from(this.querySelectorAll('[data-ref="bundle-offer"]'));

    if (this.offers.length === 0) return;

    const radioName = `${this.id || 'bundle-builder'}-offer`;
    this.offers.forEach((offer) => {
      const input = offer.querySelector('[data-ref="bundle-offer-input"]');
      if (input) input.name = radioName;
      this.offerCatalogs.set(offer, this.getOfferCatalogs(offer));
      this.initializeOfferProducts(offer);
      this.getOfferSlots(offer).forEach((slot) => this.updateSlot(slot));
    });

    this.addEventListener('change', this.handleChange);
    document.addEventListener('shopify:block:select', this.handleShopifyBlockSelect);

    const selectedInput = this.querySelector('[data-ref="bundle-offer-input"]:checked');
    const requestedPosition = Number.parseInt(this.dataset.defaultOfferPosition, 10) || 1;
    const defaultIndex = Math.min(Math.max(requestedPosition - 1, 0), this.offers.length - 1);
    const initialOffer = selectedInput?.closest('[data-ref="bundle-offer"]') || this.offers[defaultIndex];

    this.selectOffer(initialOffer, { focus: false, announce: false });
    this.updateAccessories();
    this.dataset.ready = 'true';
    this.update({ announce: false });
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleChange);
    document.removeEventListener('shopify:block:select', this.handleShopifyBlockSelect);
    if (this.productForm) {
      delete this.productForm.dataset.bundleStickyMobile;
    }
  }

  isActive() {
    return this.dataset.ready === 'true' && this.offers.length > 0;
  }

  handleChange(event) {
    const offerInput = event.target.closest?.('[data-ref="bundle-offer-input"]');
    if (offerInput) {
      const offer = offerInput.closest('[data-ref="bundle-offer"]');
      this.selectOffer(offer, { focus: false, announce: false });
      this.update({ announce: false });
      return;
    }

    if (event.target.matches?.('[data-ref="bundle-product-select"]')) {
      const slot = event.target.closest('[data-ref="bundle-slot"]');
      const offer = slot?.closest('[data-ref="bundle-offer"]');
      this.updateOfferProductAvailability(offer);
      this.updateSlot(slot);
      this.update({ announce: false });
      return;
    }

    if (event.target.matches?.('[data-ref="bundle-option-group"] input, [data-ref="bundle-option-group"] select')) {
      const slot = event.target.closest('[data-ref="bundle-slot"]');
      this.updateSlot(slot);
      this.update({ announce: false });
      return;
    }

    if (event.target.matches?.('[data-ref="bundle-accessory-toggle"], [data-ref="bundle-accessory-variant"]')) {
      this.updateAccessories();
      this.update({ announce: false });
    }
  }

  handleShopifyBlockSelect(event) {
    const offer = event.target?.closest?.('[data-ref="bundle-offer"]');
    if (!offer || !this.contains(offer)) return;

    this.selectOffer(offer, { focus: false, announce: false });
    this.update({ announce: false });
  }

  selectOffer(offer, { focus = false, announce = false } = {}) {
    if (!offer || !this.offers.includes(offer)) return;

    this.activeOffer = offer;
    const input = offer.querySelector('[data-ref="bundle-offer-input"]');
    const configuration = offer.querySelector('[data-ref="bundle-configuration"]');

    if (input) {
      input.checked = true;
      if (focus) input.focus({ preventScroll: true });
    }

    this.offers.forEach((candidate) => {
      const candidateConfiguration = candidate.querySelector('[data-ref="bundle-configuration"]');
      const candidateInput = candidate.querySelector('[data-ref="bundle-offer-input"]');
      if (candidateConfiguration) candidateConfiguration.hidden = candidate !== offer;
      if (candidateInput?.hasAttribute('aria-controls')) {
        candidateInput.setAttribute('aria-expanded', String(candidate === offer));
      }
    });

    if (configuration) {
      configuration.querySelectorAll('[data-ref="bundle-slot"]').forEach((slot) => this.updateSlot(slot));
    }

    if (announce) this.clearMessage();
  }

  update({ announce = false } = {}) {
    this.pendingBundleId = null;
    this.updateOfferPrices();
    this.updateFreeBadges();
    this.updateSummary();
    this.syncAddToCartButton();

    if (announce) this.validate({ announce: true });

    this.dispatchEvent(
      new CustomEvent(BUNDLE_UPDATED_EVENT, {
        bubbles: true,
        detail: this.getCurrentTotalPrice(),
      }),
    );
  }

  updateOfferPrices() {
    this.offers.forEach((offer) => {
      const totals = this.calculateOffer(offer);
      const price = offer.querySelector('[data-ref="bundle-offer-price"]');
      const compare = offer.querySelector('[data-ref="bundle-offer-compare"]');

      if (price) price.innerHTML = formatMoney(totals.price);
      this.updateComparePrice(compare, totals.compareAtPrice, totals.price);
    });
  }

  updateSummary() {
    const totals = this.getCurrentTotalPrice();
    const price = this.querySelector('[data-ref="bundle-summary-price"]');
    const compare = this.querySelector('[data-ref="bundle-summary-compare"]');
    const savings = this.querySelector('[data-ref="bundle-savings"]');
    const savingsValue = this.querySelector('[data-ref="bundle-savings-value"]');

    if (price) price.innerHTML = formatMoney(totals.price);
    this.updateComparePrice(compare, totals.compare_at_price, totals.price);

    if (savings && savingsValue) {
      savings.hidden = totals.difference_price <= 0;
      savingsValue.innerHTML = totals.difference_price > 0 ? formatMoney(totals.difference_price) : '';
    }
  }

  updateComparePrice(element, compareAtPrice, price) {
    if (!element) return;

    const shouldShow = compareAtPrice > price;
    element.hidden = !shouldShow;
    element.innerHTML = shouldShow ? formatMoney(compareAtPrice) : '';
  }

  initializeOfferProducts(offer) {
    if (!offer) return;

    const selects = this.getOfferSlots(offer)
      .map((slot) => slot.querySelector('[data-ref="bundle-product-select"]'))
      .filter(Boolean);
    const allowDuplicates = offer.dataset.allowDuplicateProducts !== 'false';

    if (!allowDuplicates) {
      const usedProductIds = new Set();
      selects.forEach((select) => {
        const availableOptions = Array.from(select.options).filter(
          (option) => option.dataset.productAvailable !== 'false',
        );
        const selectedOption = availableOptions.find((option) => option.value === select.value);
        const nextOption =
          (selectedOption && !usedProductIds.has(selectedOption.value) ? selectedOption : null) ||
          availableOptions.find((option) => !usedProductIds.has(option.value));

        if (nextOption) select.value = nextOption.value;
        if (select.value) usedProductIds.add(select.value);
      });
    }

    this.updateOfferProductAvailability(offer);
  }

  updateOfferProductAvailability(offer) {
    if (!offer) return;

    const selects = this.getOfferSlots(offer)
      .map((slot) => slot.querySelector('[data-ref="bundle-product-select"]'))
      .filter(Boolean);
    const allowDuplicates = offer.dataset.allowDuplicateProducts !== 'false';
    const selectedProductIds = selects.map((select) => select.value).filter(Boolean);

    selects.forEach((select) => {
      Array.from(select.options).forEach((option) => {
        const unavailable = option.dataset.productAvailable === 'false';
        const selectedElsewhere = selectedProductIds.some(
          (productId, index) => selects[index] !== select && productId === option.value,
        );
        option.disabled = unavailable || (!allowDuplicates && selectedElsewhere && option.value !== select.value);
      });
    });
  }

  syncSlotProductPanel(slot) {
    if (!slot) return null;

    const productId = this.getSlotProductId(slot);
    slot.dataset.productId = productId;
    let activePanel = null;

    slot.querySelectorAll('[data-ref="bundle-product-panel"]').forEach((panel) => {
      const isActive = panel.dataset.productId === productId;
      panel.hidden = !isActive;
      if (isActive) activePanel = panel;
    });

    return activePanel;
  }

  updateSlot(slot) {
    if (!slot) return;

    this.syncSlotProductPanel(slot);
    this.updateSlotOptionAvailability(slot);
    const variant = this.getSlotVariant(slot);
    slot.dataset.variantId = variant?.id || '';
  }

  updateAccessories() {
    this.querySelectorAll('[data-ref="bundle-accessory"]').forEach((accessory) => {
      const variant = this.getAccessoryVariant(accessory);
      const toggle = accessory.querySelector('[data-ref="bundle-accessory-toggle"]');
      const price = accessory.querySelector('[data-ref="bundle-accessory-price"]');
      const image = accessory.querySelector('[data-ref="bundle-accessory-image"]');
      const available = Boolean(variant?.available);

      accessory.classList.toggle('bundle-builder__accessory--unavailable', !available);
      if (toggle) {
        toggle.disabled = !available;
        if (!available) toggle.checked = false;
      }
      if (price && variant) price.innerHTML = formatMoney(variant.price);
      if (image && variant?.image) {
        image.src = variant.image;
        image.alt = variant.title || '';
      }
    });
  }

  updateFreeBadges() {
    this.offers.forEach((offer) => {
      const freeIndexes = new Set(this.calculateOffer(offer).freeIndexes);
      this.getOfferSlots(offer).forEach((slot, index) => {
        const badge = slot.querySelector('[data-ref="bundle-free-badge"]');
        const isFreeCandidate = freeIndexes.has(index);
        slot.dataset.freeCandidate = String(isFreeCandidate);
        if (badge) badge.hidden = !isFreeCandidate;
      });
    });
  }

  calculateOffer(offer) {
    if (!offer) {
      return { price: 0, compareAtPrice: 0, grossPrice: 0, discount: 0, freeIndexes: [], variants: [] };
    }

    const variants = this.getOfferVariants(offer);
    const grossPrice = variants.reduce((sum, variant) => sum + variant.price, 0);
    const retailPrice = variants.reduce(
      (sum, variant) => sum + Math.max(variant.compareAtPrice, variant.price),
      0,
    );
    const freeQuantity = Math.min(
      Number.parseInt(offer.dataset.freeQuantity, 10) || 0,
      variants.length,
    );
    const cheapest = variants
      .map((variant, index) => ({ index, price: variant.price }))
      .sort((a, b) => a.price - b.price || b.index - a.index)
      .slice(0, freeQuantity);
    const discount = cheapest.reduce((sum, variant) => sum + variant.price, 0);
    const price = Math.max(0, grossPrice - discount);

    return {
      price,
      compareAtPrice: Math.max(retailPrice, grossPrice),
      grossPrice,
      discount,
      freeIndexes: cheapest.map((variant) => variant.index),
      variants,
    };
  }

  getCurrentTotalPrice() {
    const offerTotals = this.calculateOffer(this.activeOffer);
    const accessoryTotals = this.getSelectedAccessories().reduce(
      (totals, accessory) => {
        totals.price += accessory.variant.price;
        totals.compareAtPrice += Math.max(accessory.variant.compareAtPrice, accessory.variant.price);
        return totals;
      },
      { price: 0, compareAtPrice: 0 },
    );
    const price = offerTotals.price + accessoryTotals.price;
    const compareAtPrice = offerTotals.compareAtPrice + accessoryTotals.compareAtPrice;

    return {
      price,
      compare_at_price: compareAtPrice,
      difference_price: Math.max(0, compareAtPrice - price),
    };
  }

  getSelectedItems() {
    if (!this.activeOffer) return [];

    const bundleId = this.pendingBundleId || this.createBundleId();
    this.pendingBundleId = bundleId;
    const offerTitle = this.activeOffer.dataset.offerTitle || this.activeOffer.dataset.offerId || '';
    const freeIndexes = new Set(this.calculateOffer(this.activeOffer).freeIndexes);
    const items = this.getOfferVariants(this.activeOffer).map((variant, index) => {
      return {
        id: variant.id,
        quantity: 1,
        properties: {
          _bundle_id: bundleId,
          _bundle_role: 'product',
          _bundle_role_label: this.dataset.bundleProductLabel || '',
          _bundle_offer: offerTitle,
          _bundle_position: String(index + 1),
          _bundle_product_id: String(variant.productId || ''),
          _bundle_free_candidate: String(freeIndexes.has(index)),
        },
      };
    });

    this.getSelectedAccessories().forEach(({ variant }, index) => {
      items.push({
        id: variant.id,
        quantity: 1,
        properties: {
          _bundle_id: bundleId,
          _bundle_role: 'accessory',
          _bundle_role_label: this.dataset.accessoryLabel || '',
          _bundle_offer: offerTitle,
          _bundle_position: String(index + 1),
          _bundle_free_candidate: 'false',
        },
      });
    });

    return items;
  }

  validate({ announce = true } = {}) {
    this.clearValidationState();

    if (!this.activeOffer) {
      return this.failValidation(this.dataset.messageChooseOffer, null, announce);
    }

    const offerSlots = this.getOfferSlots(this.activeOffer);
    const expectedQuantity = Number.parseInt(this.activeOffer.dataset.totalQuantity, 10) || 1;
    const slots =
      offerSlots.length > 0
        ? offerSlots
        : Array.from({ length: expectedQuantity }, () => this.activeOffer);
    if (offerSlots.length > 0 && this.activeOffer.dataset.allowDuplicateProducts === 'false') {
      const productIds = offerSlots.map((slot) => this.getSlotProductId(slot));
      const duplicateIndex = productIds.findIndex(
        (productId, index) => productId && productIds.indexOf(productId) !== index,
      );
      if (duplicateIndex >= 0) {
        return this.failValidation(
          this.dataset.messageChooseUniqueProduct,
          slots[duplicateIndex],
          announce,
        );
      }
    }
    const selectedVariants =
      offerSlots.length > 0
        ? offerSlots.map((slot) => this.getSlotVariant(slot))
        : this.getOfferVariants(this.activeOffer);
    if (selectedVariants.length !== expectedQuantity) {
      return this.failValidation(this.dataset.messageChooseVariant, slots[0], announce);
    }
    const invalidIndex = selectedVariants.findIndex((variant) => !variant || !variant.available);
    if (invalidIndex >= 0) {
      return this.failValidation(this.dataset.messageChooseVariant, slots[invalidIndex], announce);
    }

    const selectedAccessories = this.getSelectedAccessories({ includeUnavailable: true });
    const invalidAccessory = selectedAccessories.find(({ variant }) => !variant?.available);
    if (invalidAccessory) {
      return this.failValidation(
        this.dataset.messageAccessoryUnavailable,
        invalidAccessory.accessory,
        announce,
      );
    }

    const selections = [
      ...selectedVariants.map((variant, index) => ({ variant, target: slots[index] })),
      ...selectedAccessories.map(({ variant, accessory }) => ({ variant, target: accessory })),
    ];
    const quantities = new Map();
    selections.forEach(({ variant }) => {
      quantities.set(variant.id, (quantities.get(variant.id) || 0) + 1);
    });

    for (const { variant, target } of selections) {
      const exceedsStock =
        variant.inventoryManaged &&
        variant.inventoryPolicy !== 'continue' &&
        quantities.get(variant.id) > variant.inventoryQuantity;

      if (exceedsStock) {
        selections.forEach((selection) => {
          if (selection.variant.id === variant.id) {
            selection.target.classList.add('bundle-builder__slot--error');
            const status = selection.target.querySelector('[data-ref="bundle-slot-status"]');
            if (status) status.textContent = this.dataset.messageStockLimit || '';
          }
        });
        return this.failValidation(this.dataset.messageStockLimit, target, announce);
      }
    }

    if (announce) this.clearMessage();
    return true;
  }

  failValidation(message, target, announce) {
    if (target) target.classList.add('bundle-builder__slot--error');
    if (announce) {
      this.setMessage(message || this.dataset.messageAddError, 'error');
      const focusTarget = target?.querySelector(
        '.swatch-input:checked, .variant-picker__option-value input:checked, select, [data-ref="bundle-accessory-toggle"], [data-ref="bundle-offer-input"]',
      );
      focusTarget?.focus({ preventScroll: true });
      target?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    return false;
  }

  clearValidationState() {
    (this.productForm || this).querySelectorAll('.bundle-builder__slot--error').forEach((element) => {
      element.classList.remove('bundle-builder__slot--error');
    });
    this.querySelectorAll('[data-ref="bundle-slot-status"]').forEach((element) => {
      element.textContent = '';
    });
  }

  syncAddToCartButton() {
    if (!this.productForm?.addToCartButton || !this.isActive()) return;

    const button = this.productForm.addToCartButton;
    const price = this.productForm.addToCartButtonContainer?.querySelector('[data-ref="price"]');
    const compare = this.productForm.addToCartButtonContainer?.querySelector('[data-ref="compare-at-price"]');
    const totals = this.getCurrentTotalPrice();
    const isGiftOnly = Boolean(window.__giftOnlyProducts?.[this.dataset.productId]);
    const isValid = this.validate({ announce: false });

    button.disabled = this.isSubmitting || isGiftOnly || !isValid;
    button.setAttribute('aria-disabled', String(button.disabled));
    if (price) price.innerHTML = formatMoney(totals.price);
    this.updateComparePrice(compare, totals.compare_at_price, totals.price);
  }

  setLoading(isLoading) {
    this.isSubmitting = isLoading;
    this.toggleAttribute('aria-busy', isLoading);
    this.syncAddToCartButton();
  }

  handleAddSuccess() {
    this.pendingBundleId = null;
    this.setMessage(this.dataset.messageSuccess, 'success');
  }

  handleAddError(message) {
    this.setMessage(message || this.dataset.messageAddError, 'error');
  }

  setMessage(message, type = 'error') {
    if (!this.message) return;
    this.message.textContent = message || '';
    this.message.dataset.type = type;
  }

  clearMessage() {
    this.setMessage('', 'error');
  }

  getOfferSlots(offer) {
    const configuration = offer?.querySelector('[data-ref="bundle-configuration"]');
    return configuration ? Array.from(configuration.querySelectorAll('[data-ref="bundle-slot"]')) : [];
  }

  getOfferVariants(offer) {
    if (!offer) return [];

    const slots = this.getOfferSlots(offer);
    if (slots.length > 0) {
      return slots.map((slot) => this.getSlotVariant(slot)).filter(Boolean);
    }

    const catalog = this.getOfferCatalog(offer);
    if (catalog.length === 1) {
      const quantity = Number.parseInt(offer.dataset.totalQuantity, 10) || 1;
      return Array.from({ length: quantity }, () => catalog[0]);
    }

    return [];
  }

  getOfferCatalogs(offer) {
    const catalogs = new Map();

    offer.querySelectorAll('[data-ref="bundle-product-catalog"]').forEach((source) => {
      const productId = source.dataset.productId || '';
      if (!productId || !source.textContent) return;

      try {
        const variants = JSON.parse(source.textContent)
          .map((variant) => this.normalizeVariant(variant))
          .filter(Boolean);
        catalogs.set(productId, variants);
      } catch (error) {
        console.error(`Bundle Builder: unable to parse the variant catalog for product ${productId}.`, error);
      }
    });

    return catalogs;
  }

  getOfferCatalog(offer, productId = '') {
    const catalogs = this.offerCatalogs.get(offer);
    if (!catalogs || catalogs.size === 0) return [];
    if (productId && catalogs.has(String(productId))) return catalogs.get(String(productId));
    return catalogs.values().next().value || [];
  }

  normalizeVariant(source) {
    if (!source) return null;

    const id = Number.parseInt(source.id, 10);
    if (!id) return null;
    const price = Number.parseInt(source.price, 10) || 0;
    const compareAtPrice = Number.parseInt(source.compareAtPrice ?? source.compare_at_price, 10) || price;
    const sourceOptions = Array.isArray(source.options)
      ? source.options
      : [source.option1, source.option2, source.option3].filter((option) => option != null);

    return {
      id,
      productId: Number.parseInt(source.productId ?? source.product_id, 10) || 0,
      options: sourceOptions.map(String),
      price,
      compareAtPrice,
      title: source.title || '',
      image:
        source.image || source.featured_image?.src || source.featured_media?.preview_image?.src || '',
      available: source.available !== false,
      inventoryManaged: source.inventoryManaged ?? Boolean(source.inventory_management),
      inventoryPolicy: source.inventoryPolicy ?? source.inventory_policy ?? '',
      inventoryQuantity: Number.parseInt(source.inventoryQuantity ?? source.inventory_quantity, 10) || 0,
    };
  }

  getSlotVariant(slot) {
    if (!slot) return null;

    const offer = slot.closest('[data-ref="bundle-offer"]');
    const catalog = this.getOfferCatalog(offer, this.getSlotProductId(slot));
    const optionValues = this.getSelectedOptionValues(slot);
    if (optionValues.length === 0) return catalog.length === 1 ? catalog[0] : null;

    return (
      catalog.find(
        (variant) =>
          variant.options.length === optionValues.length &&
          variant.options.every((option, index) => option === optionValues[index]),
      ) || null
    );
  }

  getSlotProductId(slot) {
    const select = slot?.querySelector('[data-ref="bundle-product-select"]');
    return select?.value || slot?.dataset.productId || '';
  }

  getActiveProductPanel(slot) {
    const productId = this.getSlotProductId(slot);
    return Array.from(slot?.querySelectorAll('[data-ref="bundle-product-panel"]') || []).find(
      (panel) => panel.dataset.productId === productId,
    );
  }

  getSelectedOptionValues(slot) {
    const panel = this.getActiveProductPanel(slot);
    return Array.from(panel?.querySelectorAll('[data-ref="bundle-option-group"]') || [])
      .sort((a, b) => Number.parseInt(a.dataset.optionPosition, 10) - Number.parseInt(b.dataset.optionPosition, 10))
      .map((group) => {
        const selected = group.querySelector('input:checked, select');
        return selected?.value || '';
      });
  }

  updateSlotOptionAvailability(slot) {
    const offer = slot?.closest('[data-ref="bundle-offer"]');
    const catalog = this.getOfferCatalog(offer, this.getSlotProductId(slot));
    const panel = this.getActiveProductPanel(slot);
    const groups = Array.from(panel?.querySelectorAll('[data-ref="bundle-option-group"]') || []);
    const selectedValues = this.getSelectedOptionValues(slot);

    groups.forEach((group) => {
      const optionIndex = (Number.parseInt(group.dataset.optionPosition, 10) || 1) - 1;
      group.querySelectorAll('input').forEach((input) => {
        const hasAvailableVariant = catalog.some(
          (variant) =>
            variant.available &&
            variant.options[optionIndex] === input.value &&
            selectedValues.every(
              (selectedValue, index) =>
                !selectedValue || index === optionIndex || variant.options[index] === selectedValue,
            ),
        );

        input.setAttribute('aria-disabled', String(!hasAvailableVariant));
        input.labels?.[0]?.classList.toggle('swatch--disabled', !hasAvailableVariant);
      });
    });
  }

  getAccessoryVariant(accessory) {
    const control = accessory?.querySelector('[data-ref="bundle-accessory-variant"]');
    return this.getVariantFromControl(control);
  }

  getVariantFromControl(control) {
    if (!control) return null;

    const source = control.tagName === 'SELECT' ? control.selectedOptions[0] : control;
    if (!source) return null;

    return {
      id: Number.parseInt(source.dataset.variantId || source.value, 10),
      price: Number.parseInt(source.dataset.price, 10) || 0,
      compareAtPrice: Number.parseInt(source.dataset.compareAtPrice, 10) || Number.parseInt(source.dataset.price, 10) || 0,
      title: source.dataset.title || source.textContent?.trim() || '',
      image: source.dataset.image || '',
      available: source.dataset.available !== 'false' && !source.disabled,
      inventoryManaged: source.dataset.inventoryManaged === 'true',
      inventoryPolicy: source.dataset.inventoryPolicy || '',
      inventoryQuantity: Number.parseInt(source.dataset.inventoryQuantity, 10) || 0,
    };
  }

  getSelectedAccessories({ includeUnavailable = false } = {}) {
    return Array.from(this.querySelectorAll('[data-ref="bundle-accessory"]'))
      .map((accessory) => ({
        accessory,
        toggle: accessory.querySelector('[data-ref="bundle-accessory-toggle"]'),
        variant: this.getAccessoryVariant(accessory),
      }))
      .filter(({ toggle, variant }) => toggle?.checked && (includeUnavailable || variant?.available));
  }

  createBundleId() {
    if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID();
    return `${this.dataset.productId || 'bundle'}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }
}

if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}
